import React from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { vi } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { PeriodType } from '../hooks/useFreeKeys';
import { Card, CardContent } from '../../../components/ui/card';
import { Calendar as CalendarIcon } from 'lucide-react';

// Register locale once
registerLocale('vi', vi);

interface PeriodSelectorProps {
    periodType: PeriodType;
    setPeriodType: (type: PeriodType) => void;
    selectedDate: Date | null;
    handleDateChange: (date: Date | null) => void;
    periodValue: string;
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
    periodType, setPeriodType, selectedDate, handleDateChange, periodValue
}) => {
    const { t } = useTranslation('common');

    const getDatePickerProps = () => {
        const baseProps: any = {
            selected: selectedDate,
            onChange: handleDateChange,
            className: 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm',
            dateFormat: '',
            calendarClassName: 'shadow-lg border rounded-lg',
            locale: 'vi',
        };

        if (periodType === 'year') {
            baseProps.dateFormat = 'yyyy';
            baseProps.showYearDropdown = true;
            baseProps.dropdownMode = 'select';
            baseProps.scrollableYearDropdown = true;
            baseProps.yearDropdownItemNumber = 25;
            baseProps.showYearPicker = true;
        } else if (periodType === 'month') {
            baseProps.dateFormat = 'MM/yyyy';
            baseProps.showMonthYearPicker = true;
        } else if (periodType === 'week') {
            baseProps.dateFormat = 'dd/MM/yyyy';
            baseProps.showWeekNumbers = true;
            baseProps.shouldCloseOnSelect = true;
            baseProps.selectsStart = true;
            baseProps.selectsEnd = true;
        } else {
            baseProps.dateFormat = 'dd/MM/yyyy';
            baseProps.showYearDropdown = true;
            baseProps.scrollableYearDropdown = true;
        }

        return baseProps;
    };

    return (
        <Card className="mb-6 bg-white shadow-sm border-blue-50">
            <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-end">

                    {/* Type Selector */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            {t('freeKeys.period.type')}
                        </label>
                        <div className="relative">
                            <select
                                value={periodType}
                                onChange={(e) => setPeriodType(e.target.value as PeriodType)}
                                className="w-full h-10 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm appearance-none cursor-pointer"
                            >
                                <option value="year">{t('freeKeys.period.year')}</option>
                                <option value="month">{t('freeKeys.period.month')}</option>
                                <option value="week">{t('freeKeys.period.week')}</option>
                                <option value="day">{t('freeKeys.period.day')}</option>
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Date Picker */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <CalendarIcon size={16} className="text-blue-500" />
                            {t('freeKeys.period.value')}
                        </label>
                        <div className="relative">
                            <DatePicker {...getDatePickerProps()} />
                        </div>
                    </div>

                    {/* Value Display */}
                    <div className="py-2 px-4 bg-blue-50 rounded-lg border border-blue-100 text-blue-700 text-sm font-medium h-10 flex items-center justify-center">
                        {periodValue ? (
                            t('freeKeys.period.selected', { value: periodValue })
                        ) : '-'}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
